import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import { useStripePrices } from '../../services/priceConfig';
import Spinner from '../UI/Spinner';
import RecipeCard from './RecipeCard';

const MAX_ALTERNATIVES = 9;

// ── Tab icons ────────────────────────────────────────────────
const IconSuggestions = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M9.5 3L11.09 7.41L15.5 9L11.09 10.59L9.5 15L7.91 10.59L3.5 9L7.91 7.41L9.5 3ZM19 9L19.94 11.56L22.5 12.5L19.94 13.44L19 16L18.06 13.44L15.5 12.5L18.06 11.56L19 9ZM19 3L19.94 5.06L22 6L19.94 6.94L19 9L18.06 6.94L16 6L18.06 5.06L19 3Z"
      stroke={active ? '#0D0C07' : '#65635D'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconMyMeals = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      stroke={active ? '#0D0C07' : '#65635D'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconCustom = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
      stroke={active ? '#0D0C07' : '#65635D'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function RecipeAlternativesDrawer({
  isOpen,
  onClose,
  dislikedRecipe,
  alternativeRecipes,
  favoriteMeals = [],
  onSelectAlternative,
  onRemoveRecipe,
  onGenerateMore,
  isLoading = false,
  isPremium = false,
  replacementsRemaining = null,
  maxReplacements = null
}) {
  const { t } = useTranslation('common');
  const prices = useStripePrices();

  const [selectedOption, setSelectedOption] = useState(null);
  const [activeTab, setActiveTab] = useState('suggestions');
  const [customMealName, setCustomMealName] = useState('');
  const [customInputFocused, setCustomInputFocused] = useState(false);

  const { containerRef, dragOffset, shouldRender, isVisible, DragHandle } = useSwipeToClose(onClose, isOpen);

  const isLimitReached = !isLoading && alternativeRecipes.length === 0;
  const canGenerateMore = !isLoading && alternativeRecipes.length > 0 && alternativeRecipes.length < MAX_ALTERNATIVES && !!onGenerateMore;

  const filteredFavourites = favoriteMeals.filter(
    f => f.mealType === dislikedRecipe?.mealType
  );
  const showFavouritesTab = filteredFavourites.length > 0;

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setActiveTab('suggestions');
      setCustomMealName('');
    }
  }, [isOpen]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelectedOption(null);
    setCustomMealName('');
    setCustomInputFocused(false);
  };

  const handleCustomNameChange = (e) => {
    const val = e.target.value;
    setCustomMealName(val);
    setSelectedOption(val.trim() ? 'custom' : null);
  };

  const handleSubmit = () => {
    if (activeTab === 'custom' && customMealName.trim()) {
      const defaultTime = dislikedRecipe?.mealType === 'Breakfast' ? '15'
                        : dislikedRecipe?.mealType === 'Lunch' ? '30' : '45';
      onSelectAlternative({
        title: customMealName.trim(),
        description: '',
        mealType: dislikedRecipe?.mealType || 'Dinner',
        cookingTime: defaultTime,
        maxStorageTime: '3',
        ingredients: [],
        isCustomMeal: true,
      });
    } else if (selectedOption === 'remove') {
      onRemoveRecipe(dislikedRecipe);
    } else if (selectedOption && selectedOption !== 'custom') {
      onSelectAlternative(selectedOption);
    }
  };

  const isOptionSelected = (option) => {
    if (option === 'remove') return selectedOption === 'remove';
    if (!selectedOption || typeof selectedOption !== 'object') return false;
    if (option.favoriteId != null && selectedOption.favoriteId != null) {
      return selectedOption.favoriteId === option.favoriteId;
    }
    return selectedOption.title === option.title;
  };

  if (!shouldRender || !dislikedRecipe) return null;

  const canSubmit = selectedOption !== null ||
    (activeTab === 'custom' && customMealName.trim().length > 0);

  const tabs = [
    'suggestions',
    ...(showFavouritesTab ? ['favourites'] : []),
    'custom',
  ];

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 z-[200] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden"
        style={{
          maxHeight: 'calc(100dvh - 44px - env(safe-area-inset-top, 0px))',
          height: 'auto',
          transform: isVisible
            ? `translate3d(0, ${Math.max(0, dragOffset)}px, 0)`
            : 'translate3d(0, 100%, 0)',
          transition: dragOffset === 0
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <DragHandle />

        {/* Close button — absolute, overlaid on the tab bar */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 flex justify-center items-center w-[36px] h-[36px] rounded-[12px] transition-colors md:hover:bg-gray-200 z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
          </svg>
        </button>

        {/* ── Tab bar — underline style, full width ── */}
        <div className="flex-shrink-0 px-0 pt-5 flex border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)]">
          {tabs.map(tab => (
            <div
              key={tab}
              onClick={() => switchTab(tab)}
              className={`flex-1 py-3 text-center justify-center flex items-center gap-1.5 border-b-[2px] cursor-pointer transition-colors ${
                activeTab === tab
                  ? 'border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-semibold'
                  : 'text-[var(--Color-Grays-Gray-7,#65635D)] font-normal border-transparent'
              }`}
            >
              <span className="flex items-center justify-center w-[16px] h-[16px]">
                {tab === 'suggestions' && <IconSuggestions active={activeTab === 'suggestions'} />}
                {tab === 'favourites' && <IconMyMeals active={activeTab === 'favourites'} />}
                {tab === 'custom' && <IconCustom active={activeTab === 'custom'} />}
              </span>
              <span className="text-[14px] leading-[28px]">
                {tab === 'suggestions' && t('replacements.tabSuggestions', 'Suggestions')}
                {tab === 'favourites' && t('replacements.tabMyMeals', 'Favourites')}
                {tab === 'custom' && t('replacements.tabCustom', 'Custom')}
              </span>
            </div>
          ))}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-[90px]">

          {/* Free-user replacement counter */}
          {!isPremium && !isLimitReached && (
            <div className="flex gap-3 items-start bg-[#FFCB00] border-[0.5px] rounded-[12px] px-4 py-3 mt-3 mb-1 mx-0">
              <p className="text-[#0d0c07] text-[14px] leading-[24px]">
                {t('replacements.costInfo.line1', { count: typeof maxReplacements === 'number' ? maxReplacements : 3 })}{' '}
                {t('replacements.costInfo.line2', { count: typeof replacementsRemaining === 'number' ? replacementsRemaining : 3 })}
              </p>
            </div>
          )}

          {/* Tab content */}
          <div className="space-y-3 mt-5 mb-5">

            {/* ── Suggestions tab ── */}
            {activeTab === 'suggestions' && (
              <>
                {/* Limit reached message */}
                {isLimitReached && (
                  <div className="flex gap-3 items-start bg-[#FFCB00] border-[0.5px] rounded-[12px] px-4 py-3">
                    <p className="text-[#0d0c07] text-[14px] leading-[24px]">
                      {t('replacements.noAlternatives', 'No replacement options available. You can still remove this meal from your plan.')}
                    </p>
                  </div>
                )}

                {/* Initial load spinner */}
                {isLoading && alternativeRecipes.length === 0 && (
                  <button disabled className="w-full p-4 text-center rounded-[12px] border-[0.5px] border-[#E2E1DF] bg-white cursor-not-allowed">
                    <div className="flex items-center justify-center gap-3">
                      <Spinner size="sm" color="#56554D" />
                      <span className="text-[#0D0C07] font-semibold text-[14px]">{t('replacements.preparingMeals', 'Preparing Replacement Meals')}</span>
                    </div>
                  </button>
                )}

                {/* Alternatives — shown even while loading more */}
                {alternativeRecipes.map((recipe, index) => (
                  <button
                    key={index}
                    className={`w-full p-4 text-left rounded-[12px] border-[0.5px] transition-colors ${
                      isOptionSelected(recipe)
                        ? 'border-[#0D0C07] bg-[#FFCB00] md:hover:bg-[#FFCB00]/90'
                        : 'border-[#0D0C07] bg-white md:hover:bg-[#F8F8F7]'
                    }`}
                    onClick={() => setSelectedOption(recipe)}
                  >
                    <div className="flex flex-col">
                      <span className="font-semibold text-[14px] leading-[24px] text-[#0D0C07]">{recipe.title}</span>
                      {recipe.cookingTime && (
                        <span className="text-[#65635D] font-medium text-[12px] leading-[20px]">
                          {t('replacements.cookingTime', 'Cooking time: {{time}} min', { time: recipe.cookingTime })}
                        </span>
                      )}
                    </div>
                    {/* Description hidden for now — kept for easy re-enable */}
                    {/* <p className="text-[#65635D] font-normal text-[12.4px] leading-[20px] mt-1">{recipe.description}</p> */}
                  </button>
                ))}

                {/* Loading-more spinner */}
                {isLoading && alternativeRecipes.length > 0 && (
                  <div className="flex items-center justify-center gap-2 py-3 text-[13px] text-[#65635D]">
                    <Spinner size="sm" color="#56554D" />
                    <span>{t('replacements.preparingMeals', 'Preparing Replacement Meals')}</span>
                  </div>
                )}

                {/* Generate 3 more button */}
                {canGenerateMore && (
                  <button
                    onClick={onGenerateMore}
                    className="w-full p-3 text-center text-[13px] font-medium rounded-[12px] border-[0.5px] border-dashed border-[#BDBCB8] bg-transparent text-[#65635D] md:hover:bg-[#F5F3F0] transition-colors"
                  >
                    {t('replacements.generateMore', '+ 3 more options')}
                  </button>
                )}
              </>
            )}

            {/* ── My Meals (favourites) tab ── */}
            {activeTab === 'favourites' && (
              <>
                {filteredFavourites.map((fav, index) => (
                  <div
                    key={fav.favoriteId}
                    onClick={() => setSelectedOption(fav)}
                    className={`rounded-[12px] cursor-pointer transition-shadow ${
                      isOptionSelected(fav) ? 'shadow-[0_0_0_2px_#FFCB00]' : ''
                    }`}
                  >
                    <RecipeCard
                      recipe={fav}
                      index={index}
                      onRecipeClick={() => setSelectedOption(fav)}
                      imagesLoading={{}}
                      isRecipeReady={true}
                      isRecipeClickable={true}
                      isSavedRecipe={true}
                      showCookingActions={false}
                      showFavoriteButton={false}
                      fullWidth={true}
                    />
                  </div>
                ))}
              </>
            )}

            {/* ── Custom tab ── */}
            {activeTab === 'custom' && (
              <div className="w-full">
                <div className={`h-[56px] rounded-[12px] relative transition-all duration-200 bg-white`}>
                  {/* Border overlay — dark when focused, subtle when idle */}
                  <div
                    aria-hidden="true"
                    className={`absolute inset-0 pointer-events-none rounded-[12px] transition-all duration-200 ${
                      customInputFocused
                        ? 'border-[1.5px] border-[#0d0c07]'
                        : 'border-[0.5px] border-[#56554d]'
                    }`}
                  />
                  <div className="flex items-center h-full px-3">
                    <div className="flex-1 relative h-full flex flex-col justify-center">
                      {/* Floating label */}
                      <span className={`absolute left-0 pointer-events-none transition-[top,font-size,line-height,color] duration-200 text-[#72726e] ${
                        customInputFocused || customMealName.length > 0
                          ? 'top-[8px] text-[10.8px] leading-[14px]'
                          : 'top-[16px] text-[16px] leading-[24px]'
                      }`}>
                        {t('replacements.customMealLabel', 'Meal name')}
                      </span>
                      <input
                        type="text"
                        value={customMealName}
                        onChange={handleCustomNameChange}
                        onFocus={() => setCustomInputFocused(true)}
                        onBlur={() => setCustomInputFocused(false)}
                        autoFocus
                        className={`w-full bg-transparent text-[16px] text-[#0d0c07] outline-none border-none focus:outline-none focus:ring-0 leading-[24px] transition-all duration-200 ${
                          customInputFocused || customMealName.length > 0
                            ? 'pt-[14px] font-semibold'
                            : 'font-normal pt-0'
                        }`}
                        style={{
                          boxShadow: 'none',
                          WebkitBoxShadow: '0 0 0 1000px white inset',
                          WebkitTextFillColor: '#0d0c07',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Remove option — persistent below all tabs */}
          <div className="mb-4">
            <button
              onClick={() => setSelectedOption('remove')}
              className={`w-full p-4 text-left rounded-[12px] border-[0.5px] transition-colors ${
                isOptionSelected('remove')
                  ? 'border-[#0D0C07] bg-[#FFCB00] md:hover:bg-[#FFCB00]/90'
                  : 'border-[#0D0C07] bg-white md:hover:bg-[#F8F8F7]'
              }`}
            >
              <h3 className={`font-semibold text-[14px] leading-[24px] ${isOptionSelected('remove') ? 'text-[#0D0C07]' : 'text-[#D40B0B]'}`}>
                {t('replacements.justRemove', 'Just remove it')}
              </h3>
              <p className="text-[#65635D] font-normal text-[12px] leading-[20px] mt-1">
                {t('replacements.removeWarning', "You won't be able to undo it. You will have 1 meal less.")}
              </p>
            </button>
          </div>

          {/* Premium card — limit reached + free user */}
          {isLimitReached && !isPremium && (
            <div className="bg-[#f3f3f2] relative rounded-[12px] border-[0.5px] border-[#e2e1df] mb-4">
              <div className="flex gap-3 items-start px-4 pt-4 pb-5">
                <div className="flex items-center justify-center h-7 py-1 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clipPath="url(#clip0_alt_premium)">
                      <path d="M16.6667 2.5V5.83333M18.3334 4.16667H15M3.33336 14.1667V15.8333M4.1667 15H2.50003M8.28086 12.9167C8.20647 12.6283 8.05615 12.3651 7.84555 12.1545C7.63494 11.9439 7.37176 11.7936 7.08336 11.7192L1.97086 10.4008C1.88364 10.3761 1.80687 10.3235 1.75221 10.2512C1.69754 10.1789 1.66797 10.0907 1.66797 10C1.66797 9.90933 1.69754 9.82113 1.75221 9.7488C1.80687 9.67646 1.88364 9.62392 1.97086 9.59917L7.08336 8.28C7.37166 8.20567 7.63477 8.05548 7.84537 7.84503C8.05596 7.63459 8.20634 7.37157 8.28086 7.08333L9.5992 1.97083C9.6237 1.88326 9.67618 1.80612 9.74863 1.75116C9.82108 1.69621 9.90951 1.66646 10.0004 1.66646C10.0914 1.66646 10.1798 1.69621 10.2523 1.75116C10.3247 1.80612 10.3772 1.88326 10.4017 1.97083L11.7192 7.08333C11.7936 7.37173 11.9439 7.63491 12.1545 7.84551C12.3651 8.05612 12.6283 8.20643 12.9167 8.28083L18.0292 9.59833C18.1171 9.62258 18.1946 9.67501 18.2499 9.74756C18.3052 9.82012 18.3351 9.9088 18.3351 10C18.3351 10.0912 18.3052 10.1799 18.2499 10.2524C18.1946 10.325 18.1171 10.3774 18.0292 10.4017L12.9167 11.7192C12.6283 11.7936 12.3651 11.9439 12.1545 12.1545C11.9439 12.3651 11.7936 12.6283 11.7192 12.9167L10.4009 18.0292C10.3764 18.1167 10.3239 18.1939 10.2514 18.2488C10.179 18.3038 10.0905 18.3335 9.99961 18.3335C9.90868 18.3335 9.82025 18.3038 9.7478 18.2488C9.67535 18.1939 9.62287 18.1167 9.59836 18.0292L8.28086 12.9167Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_alt_premium"><rect width="20" height="20" fill="white"/></clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="flex flex-col flex-grow">
                  <div className="flex flex-col gap-1">
                    <p className="text-[#0d0c07] text-[15.2px] leading-[28px] font-bold">{t('premium.becomePremium')}</p>
                    <p className="text-[#006600] text-[14px] leading-[24px]">{t('premium.onlyPrice', { price: prices.yearlyPerMonth })}</p>
                  </div>
                  <Link to="/premium" className="text-[#0d0c07] font-semibold text-[14px] leading-[24px] border-b border-[#ffcb00] inline-block self-start" onClick={onClose}>
                    {t('premium.discoverFeatures')}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Sticky footer */}
          <div className="fixed bottom-[16px] left-[24px] right-[24px] py-2 px-2 flex justify-center bg-[linear-gradient(145deg,_rgba(255,255,255,0.9)_0%,_rgba(255,255,255,0.6)_100%)] backdrop-blur-lg z-50 mx-auto rounded-[96px] border-[0.5px] shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]">
            <div className="w-full max-w-5xl flex items-center justify-between">
              <button
                className={`inline-flex justify-center items-center gap-1 h-[48px] px-[24px] rounded-[32px] text-[#FFF] text-[14px] font-medium leading-[28px] transition-colors w-full bg-[linear-gradient(200deg,_#0D0C07,_#353227_100%)] ${
                  canSubmit ? 'md:hover:bg-[#090]' : 'opacity-50 cursor-not-allowed'
                } shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(13,12,7,0.4)]`}
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {t('replacements.update', 'Update')}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>, document.body
  );
}

export default RecipeAlternativesDrawer;
